<template>
    <div class="pv">
        <div class="pv-head">
            <span class="pv-title">{{ $t('unit.preview.title') }}</span>
            <!-- ⚠ 找單字那一格換文案：它不是老師編的分鏡，是學生玩完自動存下來的
                 那一篇紀錄，「第 1 格」對老師沒有意義。判斷在上層（UnitQuestEdit
                 的 `isGameRecord`），這裡只收結果——同一套規則寫兩份遲早會不一致。 -->
            <span v-if="frame" class="pv-sub">
                {{ gameRecord ? $t('unit.frame.wordsearch_no')
                              : $t('unit.frame.panel_n', { n: index + 1 }) }}
            </span>
        </div>

        <div class="pv-body">
            <div v-if="!frame" class="pv-todo">{{ $t('unit.preview.pending') }}</div>

            <template v-else>
                <div class="sc">
                    <div class="sc-head">
                        <span class="sc-type">
                            {{ gameRecord ? $t('unit.frame.wordsearch_type')
                                          : $t(`quest.main.${frame.type}_frame`) }}
                        </span>
                        <span class="sc-bar">|</span>
                        <span class="sc-title" :class="{ 'is-empty': !frame.title }">
                            {{ frame.title || $t('unit.frame.unnamed') }}
                        </span>
                    </div>

                    <!-- 行動畫格：學生端是兩步（先選類型、才看到範例） -->
                    <template v-if="frame.type === 'behavior'">
                        <div class="sc-step">{{ $t('unit.preview.step1') }}</div>
                        <div class="sc-tags">
                            <span v-for="bt in behaviorTypes" :key="bt"
                                class="sc-tag"
                                :class="{
                                    'sc-tag--has': behaviorCount(bt) > 0,
                                    'sc-tag--on': bt === behaviorType,
                                }">
                                {{ $t(`behavior.type.${bt}`) }}
                            </span>
                        </div>
                        <div v-if="behaviorType !== null" class="sc-step">
                            {{ $t('unit.preview.step2',
                                { type: $t(`behavior.type.${behaviorType}`) }) }}
                        </div>
                    </template>

                    <!-- 圖片畫格不畫上傳框，只顯示它的句型範例（卡外那行說明已經講了上傳） -->

                    <!-- 心情畫格：沒有預設心情清單可以顯示（那是學生端提供的） -->
                    <div v-if="frame.type === 'feeling'" class="sc-note-in">
                        {{ $t('unit.preview.mood_note') }}
                    </div>

                    <template v-else>
                        <!-- 作答框：欄位以 (名稱) 呈現，當前選取的加深 -->
                        <div class="sc-answer">
                            <template v-if="tokens.length">
                                <template v-for="(tk, i) in tokens">
                                    <span v-if="tk.kind === 'text'" :key="i">{{ tk.v }}</span>
                                    <span v-else :key="i" class="sc-field"
                                        :class="{ 'sc-field--on': tk.name === activeFieldName }">
                                        （{{ tk.name }}）
                                    </span>
                                </template>
                            </template>
                            <span v-else class="sc-empty">{{ emptySentenceText }}</span>
                        </div>

                        <!-- 學生點該欄位時會看到什麼 -->
                        <template v-if="activeField">
                            <div class="sc-hint">{{ $t('unit.preview.field_hint') }}</div>
                            <div class="sc-panel">
                                <div v-if="activeField.mode === 'free'" class="sc-free">
                                    {{ $t('unit.preview.free_input') }}
                                </div>
                                <div v-else class="sc-opts">
                                    <span v-for="(op, oi) in activeField.options" :key="oi"
                                        class="sc-opt">{{ op }}</span>
                                </div>
                            </div>
                        </template>

                        <template v-if="sampleList.length">
                            <div class="sc-hint">{{ $t('unit.preview.samples_label') }}</div>
                            <div class="sc-samples">
                                <span v-for="(sp, si) in sampleList" :key="si" class="sc-sample">
                                    # {{ plainText(sp) }}
                                </span>
                            </div>
                        </template>
                    </template>

                    <div class="sc-next">{{ $t('unit.preview.next') }}</div>
                </div>

                <div class="pv-note">{{ typeNote }}</div>
            </template>
        </div>
    </div>
</template>

<script>
const BEHAVIOR_TYPES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

/**
 * 學生端示意（設計稿 §6）。
 *
 * 刻意做成**單色線框示意**——不模擬學生端 App 的外框與品牌色，
 * 設計稿試過完整擬真，因為太搶眼而移除。這裡只是讓老師確認「學生會看到什麼」。
 *
 * 只讀，不改資料；所有狀態都由 UnitQuestEdit 傳進來。
 */
export default {
    name: 'StudentPreview',
    props: {
        /// 目前選取的畫格；null 表示還沒選
        frame: { type: Object, default: null },
        index: { type: Number, default: 0 },
        /**
         * 這一格是不是「找單字玩完自動存下來的紀錄」。
         * ⚠ **判斷不在這裡**：規則是「找單字 + 第一格 + type 是 story」，由 UnitQuestEdit
         * 的 `isGameRecord` 決定。這個元件只負責呈現，收一個布林比自己再判斷一次安全
         * ——同一套規則寫兩份，改了一邊就會前後文案不一致。
         */
        gameRecord: { type: Boolean, default: false },
        /// 目前這一組（behavior 是該行動類型底下）的範例
        sampleList: { type: Array, default: () => [] },
        /// 正在編輯的那一張範例卡
        activeSample: { type: Object, default: null },
        /// ExampleCard 選到的欄位名稱，用來加深對應的標記
        activeFieldName: { type: String, default: null },
        behaviorType: { type: Number, default: null },
        /// { [type]: 範例數 }，決定行動類型標籤是實線還是虛線
        behaviorCountMap: { type: Object, default: () => ({}) },
    },
    data() {
        return { behaviorTypes: BEHAVIOR_TYPES };
    },
    computed: {
        /// 沒有正在編輯的卡就退回顯示這一組的第一個範例
        shownSample() {
            return this.activeSample || this.sampleList[0] || null;
        },
        tokens() {
            return this.shownSample ? this.split(this.shownSample) : [];
        },
        activeField() {
            const sp = this.shownSample;
            if (!sp || !this.activeFieldName) {
                return null;
            }
            const found = (sp.option || []).find((o) => o.field === this.activeFieldName);
            if (!found) {
                return null;
            }
            const options = Array.isArray(found.options) ? found.options : [];
            /// 0 個選項 = 自由填寫（mode 不進資料庫，見 steps 決策表）
            return { options, mode: options.length ? 'options' : 'free' };
        },
        emptySentenceText() {
            return this.sampleList.length
                ? this.$t('unit.preview.empty_sentence')
                : this.$t('unit.preview.no_sample');
        },
        typeNote() {
            const map = {
                picture: 'note_picture',
                feeling: 'note_feeling',
                behavior: 'note_behavior',
            };
            const key = this.frame ? map[this.frame.type] : null;
            return this.$t(`unit.preview.${key || 'note_write'}`);
        },
    },
    methods: {
        behaviorCount(type) {
            return this.behaviorCountMap[type] || 0;
        },

        /** 與 ExampleCard 同一條規則：只認 option[] 列出的欄位名，不做通用括號解析 */
        split(sample) {
            const names = (sample.option || []).map((o) => o.field);
            const out = [];
            const used = [];
            let rest = sample.content || '';
            let guard = 0;
            while (guard++ < 200) {
                let at = -1;
                let hit = '';
                names.forEach((name) => {
                    if (used.includes(name)) {
                        return;
                    }
                    const i = rest.indexOf(`(${name})`);
                    if (i !== -1 && (at === -1 || i < at)) {
                        at = i;
                        hit = name;
                    }
                });
                if (at === -1) {
                    break;
                }
                if (at > 0) {
                    out.push({ kind: 'text', v: rest.slice(0, at) });
                }
                out.push({ kind: 'field', name: hit });
                used.push(hit);
                rest = rest.slice(at + hit.length + 2);
            }
            if (rest) {
                out.push({ kind: 'text', v: rest });
            }
            return out;
        },

        /** 範例清單只要看得懂就好，欄位用（名稱）帶過 */
        plainText(sample) {
            const text = this.split(sample)
                .map((tk) => (tk.kind === 'text' ? tk.v : `（${tk.name}）`))
                .join('');
            return text || this.$t('unit.preview.empty_sentence');
        },
    },
};
</script>

<style scoped>
.pv {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    overflow: hidden;
}

/* ⚠ 水平內距要與 UnitQuestEdit 的 `.card`（20px）一致。
   之前是 12px，所以這張卡的內容比同一頁其他卡片更貼邊，看起來像另一套版面。
   垂直用 14px：這一列是有下框線的標題列，比內文矮是刻意的（設計圖也是 14/20）。 */
.pv-head {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    border-bottom: 1px solid #eeeeee;
}

.pv-title {
    font-size: 14px;
    font-weight: 700;
    color: #212121;
}

.pv-sub {
    font-size: 12px;
    color: #9e9e9e;
    margin-left: auto;
}

/* 與 `.card` 同值（18px 20px） */
.pv-body {
    background: #fafafa;
    padding: 18px 20px;
}

.pv-todo {
    font-size: 12px;
    color: #bdbdbd;
}

.sc {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 14px 12px;
}

.sc-head {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 10px;
}

.sc-type {
    font-size: 13px;
    font-weight: 500;
    color: #212121;
}

.sc-bar {
    color: #e0e0e0;
}

.sc-title {
    font-size: 12px;
    color: #757575;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sc-title.is-empty {
    color: #bdbdbd;
}

.sc-step {
    font-size: 11px;
    color: #9e9e9e;
    margin: 8px 0 4px;
}

.sc-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

/* 虛線＝該類型還沒有範例，學生點進去會是空的 */
.sc-tag {
    font-size: 11px;
    border-radius: 11px;
    padding: 4px 9px;
    border: 1px dashed #eeeeee;
    color: #bdbdbd;
}

.sc-tag--has {
    border: 1px solid #e0e0e0;
    color: #757575;
}

.sc-tag--on {
    border: 1px solid #90caf9;
    background: #e3f2fd;
    color: #1565c0;
}

.sc-note-in {
    font-size: 12px;
    color: #9e9e9e;
    line-height: 1.7;
    margin-top: 8px;
}

.sc-answer {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 11px;
    min-height: 66px;
    margin-top: 8px;
    font-size: 13px;
    line-height: 1.9;
    color: #212121;
    word-break: break-word;
}

.sc-field {
    background: #fff3e0;
    color: #e65100;
    border-bottom: 1px dashed #ffb74d;
}

.sc-field--on {
    background: #ffe0b2;
    color: #ef6c00;
}

.sc-empty {
    color: #bdbdbd;
}

.sc-hint {
    font-size: 11px;
    color: #9e9e9e;
    margin: 10px 0 4px;
}

.sc-panel {
    border: 1px solid #eeeeee;
    border-radius: 4px;
    padding: 8px 10px;
    background: #fafafa;
}

.sc-free {
    font-size: 12px;
    color: #bdbdbd;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fff;
    padding: 7px 9px;
}

.sc-opts {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.sc-opt {
    font-size: 12px;
    color: #37474f;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 6px 9px;
}

.sc-samples {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.sc-sample {
    font-size: 11px;
    color: #9e9e9e;
    word-break: break-word;
}

.sc-next {
    margin-top: 12px;
    background: #eeeeee;
    color: #bdbdbd;
    font-size: 12px;
    text-align: center;
    border-radius: 4px;
    padding: 8px;
}

.pv-note {
    font-size: 11px;
    color: #9e9e9e;
    line-height: 1.6;
    margin-top: 8px;
}
</style>
